// InvoiceBlock — блок «Счета» в карточке сделки (V14 W6, §3.6/§3.8; канон PaymentBlock).
// Сводка (Итого/Оплачено/Остаток — Σ по счетам в валюте сделки, OQ-12) по ВСЕМ счетам
// сделки (FE-агрегация по странице size=100 — §3.6; при >100 счетов — «первые 100»,
// серверный агрегат — V14.2/OQ-12) + таблица счетов ≤5 + кнопки: «+ Счёт»
// (InvoiceNewDialog с preset сделки), «Создать платёж» (PaymentNewDialog
// с presetInvoice — «Оплатить счёт», W7 §3.8: поле «Счёт №INV-…», POST с invoiceId)
// и «Показать все →» (/invoices?dealId=).

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Box, Button, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { invoicesApi, type Invoice } from '@/api/invoices';
import { InvoiceNewDialog } from '@/pages/invoices/components/InvoiceNewDialog';
import { InvoiceStatusChip } from '@/pages/invoices/components/InvoiceStatusChip';
import { InvoiceEmailDialog } from '@/pages/invoices/components/InvoiceEmailDialog';
import { PaymentNewDialog } from '@/pages/payments/components/PaymentNewDialog';
import { formatDate, formatInvoiceNumber, formatMoney } from '@/lib/format';
import { t } from '@/i18n';

/** Сколько строк счетов показываем в таблице блока (канон §3.8: ≤5 + «Показать все»). */
const BLOCK_PAGE_SIZE = 5;

/**
 * Размер страницы для СВОДКИ (KISS-канон §3.6: FE-агрегация по size=100 — по всем счетам
 * сделки; BE-кап 100. При >100 счетов сводка покрывает «первые 100» (порядок created_at DESC) —
 * отмеченное ограничение, серверный агрегат — V14.2/OQ-12.
 */
const SUMMARY_PAGE_SIZE = 100;

/** Сводка по счетам сделки (Σ по валюте сделки — OQ-12 «счета в валютах — только равенство валюты»). */
interface InvoiceSummary {
  total: number;
  paid: number;
  remaining: number;
}

/** Считает Итого/Оплачено/Остаток по счетам (только валюта сделки; пусто → нулевая сводка). */
function computeSummary(invoices: Invoice[], dealCurrency: string | undefined): InvoiceSummary {
  const sameCurrency = dealCurrency ? invoices.filter((i) => i.currency === dealCurrency) : invoices;
  return sameCurrency.reduce(
    (acc, i) => ({
      total: acc.total + i.amount,
      paid: acc.paid + i.paidAmount,
      remaining: acc.remaining + i.remaining,
    }),
    { total: 0, paid: 0, remaining: 0 },
  );
}

interface InvoiceBlockProps {
  dealId: string;
  /** Валюта сделки — сводка считается по счетам этой валюты (OQ-12). */
  dealCurrency?: string;
}

/** Блок «Счета» карточки сделки: сводка по всем счетам (size=100) + до 5 строк + создание счёта/платежа. */
export function InvoiceBlock({ dealId, dealCurrency }: InvoiceBlockProps): JSX.Element {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [payInvoice, setPayInvoice] = useState<Invoice | null>(null);
  // V15 W7 (§3.8): «Отправить письмо» — только для OPEN-счёта (правило §5 п.4).
  const [sendInvoice, setSendInvoice] = useState<Invoice | null>(null);

  const query = useQuery({
    queryKey: ['invoices', 'deal', dealId],
    queryFn: () => invoicesApi.list({ dealId, page: 0, size: SUMMARY_PAGE_SIZE }),
    enabled: Boolean(dealId),
  });

  const invoices = query.data?.content ?? [];
  // Сводка — по ВСЕМ счетам сделки из запроса (size=100), таблица — только первые 5.
  const summary = computeSummary(invoices, dealCurrency);
  const tableInvoices = invoices.slice(0, BLOCK_PAGE_SIZE);

  return (
    <section aria-label={t('invoices.block.title')}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6">{t('invoices.block.title')}</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          {t('invoices.block.add')}
        </Button>
      </Stack>

      {query.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {query.error instanceof Error ? query.error.message : t('invoices.loadError')}
        </Alert>
      ) : null}

      {query.isLoading ? (
        <Typography variant="body2" color="text.secondary">
          {t('invoices.loading')}
        </Typography>
      ) : !query.data || query.data.content.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('invoices.empty')}
        </Typography>
      ) : (
        <>
          <Stack direction="row" spacing={4} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('invoices.block.total')}
              </Typography>
              <Typography variant="body2">{formatMoney(summary.total, dealCurrency)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('invoices.block.paid')}
              </Typography>
              <Typography variant="body2">{formatMoney(summary.paid, dealCurrency)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('invoices.block.remaining')}
              </Typography>
              <Typography variant="body2">{formatMoney(summary.remaining, dealCurrency)}</Typography>
            </Box>
          </Stack>
          <TableContainer>
            <Table size="small" aria-label={t('invoices.block.title')}>
              <TableHead>
                <TableRow>
                  <TableCell>№</TableCell>
                  <TableCell>{t('invoices.amount')}</TableCell>
                  <TableCell>{t('invoices.dueDate')}</TableCell>
                  <TableCell>{t('invoices.status')}</TableCell>
                  <TableCell align="right">{t('invoices.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableInvoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>{formatInvoiceNumber(invoice.invoiceNumber, invoice.invoiceDate)}</TableCell>
                    <TableCell>{formatMoney(invoice.amount, invoice.currency)}</TableCell>
                    <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                    <TableCell>
                      <InvoiceStatusChip status={invoice.status} />
                    </TableCell>
                    <TableCell align="right">
                      {/* V15 W7 (§3.8): «Отправить письмо» — OPEN-счёт (DRAFT/VOID — недоступно). */}
                      {invoice.lifecycle === 'OPEN' ? (
                        <Button size="small" aria-label={t('email.send.title')} onClick={() => setSendInvoice(invoice)}>
                          {t('email.send.title')}
                        </Button>
                      ) : null}
                      <Button size="small" aria-label={t('invoices.block.createPayment')} onClick={() => setPayInvoice(invoice)}>
                        {t('invoices.block.createPayment')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {query.data && query.data.total > BLOCK_PAGE_SIZE ? (
        <Button size="small" sx={{ mt: 1 }} onClick={() => navigate(`/invoices?dealId=${dealId}`)}>
          {t('invoices.block.showAll')}
        </Button>
      ) : null}

      <InvoiceNewDialog open={createOpen} onClose={() => setCreateOpen(false)} presetDealId={dealId} />
      <PaymentNewDialog open={Boolean(payInvoice)} onClose={() => setPayInvoice(null)} presetInvoice={payInvoice} />
      <InvoiceEmailDialog open={Boolean(sendInvoice)} onClose={() => setSendInvoice(null)} invoice={sendInvoice!} />
    </section>
  );
}
